import { memo, useMemo } from 'react';
import { useTranslation } from '@bonanza-social/i18n';
import { formatNumber } from '@bonanza-social/toolkit/utils';
import { Box, Icon, PressableOverlay, Text } from '@bonanza-social/moonlight';
import { JackpotCardProps } from './JackpotCard.types';
import { getMilestoneRewards } from '../../../../../../utils/jackpot';
import CommunityJackpotPlayer from '../../../../../CommunityJackpotPlayer/CommunityJackpotPlayer';

function JackpotCard({
  label,
  status,
  prizes,
  highlighted,
  rewardAmount,
  jackpotWinnerStatus,
  jackpotWinnerScreenName,
  onAllWinnersPress,
  onAllPrizesPress,
  ...rest
}: JackpotCardProps) {
  const { t } = useTranslation('communityJackpot');
  const rewardsLength = useMemo(() => getMilestoneRewards(prizes), [prizes]);

  if (jackpotWinnerScreenName) {
    return (
      <Box {...rest}>
        <Box marginLeft="space-800" marginRight="space-300">
          <Box
            variant="outline-gradient"
            gradient="borderEmphasis"
            marginBottom="space-300"
            height={1}
          />
          <Text
            variant="badge"
            locKey="jackpotWinner"
            locNS="communityJackpot"
            color="darkSurfacePrimary"
            paddingBottom="space-100"
            alignSelf="center"
          />
          <CommunityJackpotPlayer
            name={jackpotWinnerScreenName}
            status={jackpotWinnerStatus}
            alignSelf="center"
          />
          <Box
            variant="outline-gradient"
            gradient="borderEmphasis"
            marginTop="space-300"
            height={1}
          />
          <Text
            variant="link-small"
            locKey="seeAllWinners"
            locNS="communityJackpot"
            color="darkSurfacePrimary"
            paddingTop="space-100"
            textAlign="center"
            onPress={onAllWinnersPress}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box alignItems="center" {...rest}>
      <Box
        marginLeft="space-800"
        marginRight="space-200"
        padding="space-200"
        backgroundColor={highlighted ? 'primaryBackground' : undefined}
        borderRadius="radius-200"
        centerContent
      >
        <Text variant="badge" color="darkSurfacePrimary">
          {status ? t('jackpotWon') : label || t('milestoneJackpot')}
        </Text>
        <Box
          flexDirection="row"
          alignItems="center"
          gap="space-100"
          marginTop="space-200"
        >
          <Icon
            name="bonanzaCoin"
            gradient={status ? undefined : 'accentBackground'}
            color="iconSecondary"
            width="space-400"
            height="space-400"
          />
          <Text variant="h6" color="darkSurfacePrimary">
            {formatNumber(rewardAmount)}
          </Text>
        </Box>
        <Text
          variant={status ? 'badge' : 'link-small'}
          color="darkSurfacePrimary"
          paddingTop="space-100"
        >
          {status
            ? t('plusWinners', { count: rewardsLength })
            : t('seeAllPrizes')}
        </Text>
        <PressableOverlay onPress={onAllPrizesPress} />
      </Box>
    </Box>
  );
}

export default memo(JackpotCard);
